import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import FolderCopyRoundedIcon from '@mui/icons-material/FolderCopyRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import ViewKanbanRoundedIcon from '@mui/icons-material/ViewKanbanRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { getMe, logout } from './api';
import { BrandLockup } from './BrandLockup';
import { GlobalSearchDialog } from './GlobalSearchDialog';
import { SupportFooter } from './SupportFooter';

export interface AppOutletContext {
  user: Awaited<ReturnType<typeof getMe>>;
}

export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchOpen, setSearchOpen] = useState(false);
  const me = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false });
  const logoutMutation = useMutation({
    mutationFn: logout,
    onSettled: () => {
      queryClient.clear();
      void navigate('/login', { replace: true });
    },
  });

  if (me.isPending) {
    return (
      <Box sx={{ display: 'grid', minHeight: '100vh', placeItems: 'center' }}>
        <CircularProgress aria-label="Caricamento sessione" />
      </Box>
    );
  }
  if (me.isError) return <Navigate replace state={{ from: location.pathname }} to="/login" />;
  if (me.data.role === 'CLIENT' && location.pathname.startsWith('/crm')) {
    return <Navigate replace to="/portal" />;
  }
  if (me.data.role !== 'CLIENT' && location.pathname.startsWith('/portal')) {
    return <Navigate replace to="/crm/projects" />;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        elevation={0}
        position="sticky"
        sx={{
          bgcolor: 'primary.dark',
          color: 'common.white',
          '& .MuiButton-textInherit': { color: 'common.white' },
          '& .MuiButton-colorPrimary': {
            bgcolor: 'rgba(255, 255, 255, 0.15)',
            color: '#ffffff',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.22)' },
          },
        }}
      >
        <Toolbar
          sx={{
            borderBottom: '1px solid rgba(255, 255, 255, 0.22)',
            gap: { xs: 0.5, sm: 1, lg: 2 },
          }}
        >
          <Box sx={{ display: { xs: 'none', lg: 'block' }, mr: 1 }}>
            <BrandLockup compact />
          </Box>
          <Stack direction="row" spacing={{ xs: 0.25, sm: 1 }} sx={{ flex: 1 }}>
            {me.data.role === 'PARTNER' && (
              <>
                <Button
                  aria-label="Aziende"
                  color={location.pathname.startsWith('/crm/companies') ? 'primary' : 'inherit'}
                  onClick={() => void navigate('/crm/companies')}
                  startIcon={<BusinessRoundedIcon />}
                  sx={{ minWidth: { xs: 40, sm: 64 }, '& .MuiButton-startIcon': { mr: { xs: 0 } } }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Aziende
                  </Box>
                </Button>
                <Button
                  aria-label="Pipeline"
                  color={location.pathname.startsWith('/crm/pipeline') ? 'primary' : 'inherit'}
                  onClick={() => void navigate('/crm/pipeline')}
                  startIcon={<ViewKanbanRoundedIcon />}
                  sx={{ minWidth: { xs: 40, sm: 64 }, '& .MuiButton-startIcon': { mr: { xs: 0 } } }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Pipeline
                  </Box>
                </Button>
                <Button
                  aria-label="Amministrazione"
                  color={location.pathname.startsWith('/crm/admin') ? 'primary' : 'inherit'}
                  onClick={() => void navigate('/crm/admin')}
                  startIcon={<AdminPanelSettingsRoundedIcon />}
                  sx={{ minWidth: { xs: 40, sm: 64 }, '& .MuiButton-startIcon': { mr: { xs: 0 } } }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Admin
                  </Box>
                </Button>
              </>
            )}
            {me.data.role !== 'CLIENT' && (
              <>
                <Button
                  aria-label="Dashboard"
                  color={location.pathname.startsWith('/crm/dashboard') ? 'primary' : 'inherit'}
                  onClick={() => void navigate('/crm/dashboard')}
                  startIcon={<DashboardRoundedIcon />}
                  sx={{ minWidth: { xs: 40, sm: 64 }, '& .MuiButton-startIcon': { mr: { xs: 0 } } }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Dashboard
                  </Box>
                </Button>
                <Button
                  aria-label="Progetti"
                  color={location.pathname.startsWith('/crm/projects') ? 'primary' : 'inherit'}
                  onClick={() => void navigate('/crm/projects')}
                  startIcon={<FolderCopyRoundedIcon />}
                  sx={{ minWidth: { xs: 40, sm: 64 }, '& .MuiButton-startIcon': { mr: { xs: 0 } } }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Progetti
                  </Box>
                </Button>
                <Button
                  aria-label="Calendario"
                  color={location.pathname.startsWith('/crm/calendar') ? 'primary' : 'inherit'}
                  onClick={() => void navigate('/crm/calendar')}
                  startIcon={<EventRoundedIcon />}
                  sx={{ minWidth: { xs: 40, sm: 64 }, '& .MuiButton-startIcon': { mr: { xs: 0 } } }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Calendario
                  </Box>
                </Button>
              </>
            )}
            {me.data.role === 'CLIENT' && (
              <Button
                aria-label="Portale"
                color={location.pathname.startsWith('/portal') ? 'primary' : 'inherit'}
                onClick={() => void navigate('/portal')}
                startIcon={<VisibilityRoundedIcon />}
                sx={{ minWidth: { xs: 40, sm: 64 }, '& .MuiButton-startIcon': { mr: { xs: 0 } } }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Portale
                </Box>
              </Button>
            )}
          </Stack>
          <Button
            aria-label="Cerca"
            color="inherit"
            onClick={() => setSearchOpen(true)}
            startIcon={<SearchRoundedIcon />}
            sx={{
              minWidth: { xs: 40, sm: 64 },
              '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Cerca
            </Box>
          </Button>
          <Avatar
            sx={{
              bgcolor: 'secondary.main',
              display: { xs: 'none', sm: 'flex' },
              height: 34,
              width: 34,
            }}
          >
            {me.data.firstName.slice(0, 1)}
          </Avatar>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Typography sx={{ fontWeight: 800 }} variant="body2">
              {me.data.firstName} {me.data.lastName}
            </Typography>
            <Typography color="inherit" sx={{ opacity: 0.78 }} variant="caption">
              {me.data.role === 'PARTNER'
                ? 'Partner'
                : me.data.role === 'COLLABORATOR'
                  ? 'Collaboratore'
                  : 'Cliente'}
            </Typography>
          </Box>
          <Button
            aria-label="Esci"
            color="inherit"
            disabled={logoutMutation.isPending}
            onClick={() => logoutMutation.mutate()}
            startIcon={<LogoutRoundedIcon />}
            sx={{
              minWidth: { xs: 40, sm: 64 },
              '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Esci
            </Box>
          </Button>
        </Toolbar>
      </AppBar>
      <Box sx={{ flex: 1 }}>
        <Outlet context={{ user: me.data }} />
      </Box>
      <GlobalSearchDialog onClose={() => setSearchOpen(false)} open={searchOpen} />
      <SupportFooter />
    </Box>
  );
}
